import { useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Check, Loader2 } from "lucide-react";
import { Button, Input, LogoWordmark, PasswordInput } from "@atrix-analytics/ui";
import { api, USE_FIXTURES } from "../api";
import { ApiError, errorMessage } from "../api/http";
import { keys } from "../api/queries";
import { SignInInput, SignUpInput } from "../api/schemas";
import { Field, FormError } from "../components/field";
import { sameOriginPath } from "../lib/redirect";

export type Errors = Partial<Record<string, string>>;

export function fieldErrors(issues: { path: PropertyKey[]; message: string }[]): Errors {
  const out: Errors = {};
  for (const i of issues) out[String(i.path[0])] ??= i.message;
  return out;
}

export function AuthShell({ title, subtitle, children }: { title: string; subtitle: ReactNode; children: ReactNode }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
      <main className="flex flex-col px-4 py-8 sm:px-10">
        <LogoWordmark />
        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-10">
          <h1 className="font-display text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="mt-1.5 text-sm text-fg-muted">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
        {USE_FIXTURES && (
          <p className="text-center text-xs text-fg-faint">
            Demo mode: data is simulated in this browser and nothing is sent to a server.
          </p>
        )}
      </main>
      <aside className="hidden border-l border-border bg-bg-void lg:flex lg:flex-col lg:justify-center lg:px-14">
        <p className="font-mono text-xs tracking-[0.14em] text-fg-faint uppercase">Product analytics</p>
        <p className="mt-3 max-w-md font-display text-3xl leading-tight font-semibold tracking-tight">
          Know what your users do — and keep their data where it belongs.
        </p>
        <ul className="mt-8 space-y-4 text-sm text-fg-muted">
          {[
            ["Region you choose, for real", "Events are captured, stored and queried in the project's region."],
            ["One meter, published prices", "Pay for events. Seats and flags are free. No surprise cut-offs."],
            ["Built for apps, not just websites", "Environments, keys and SDKs designed around mobile releases."],
          ].map(([h, b]) => (
            <li key={h} className="flex gap-3">
              <Check aria-hidden className="mt-0.5 size-4 shrink-0 text-accent" />
              <span>
                <span className="font-medium text-fg">{h}.</span> {b}
              </span>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}

export function SignInPage() {
  const search = useSearch({ from: "/sign-in" });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [errors, setErrors] = useState<Errors>({});

  const mutation = useMutation({
    mutationFn: api.signIn,
    onSuccess: async (me) => {
      qc.setQueryData(keys.me, me);
      await navigate({ href: sameOriginPath(search.redirect) });
    },
    onError: (err) => {
      if (err instanceof ApiError) setErrors(err.fields);
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget));
    const parsed = SignInInput.safeParse(data);
    if (!parsed.success) return setErrors(fieldErrors(parsed.error.issues));
    setErrors({});
    mutation.mutate(parsed.data);
  };

  return (
    <AuthShell
      title="Sign in"
      subtitle={
        <>
          New to Atrix Analytics?{" "}
          <Link to="/sign-up" className="font-medium text-accent underline-offset-4 hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <FormError message={mutation.isError && !Object.keys(errors).length ? errorMessage(mutation.error) : null} />
        <Field label="Work email" error={errors.email}>
          <Input name="email" type="email" autoComplete="email" inputMode="email" required autoFocus />
        </Field>
        <Field label="Password" error={errors.password}>
          <PasswordInput name="password" autoComplete="current-password" required />
        </Field>
        <Link to="/forgot-password" className="-mt-2 self-end text-xs font-medium text-accent underline-offset-4 hover:underline">
          Forgot your password?
        </Link>
        <Button type="submit" size="lg" className="mt-2 w-full" disabled={mutation.isPending}>
          {mutation.isPending && <Loader2 className="animate-spin" aria-hidden />}
          {mutation.isPending ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </AuthShell>
  );
}

export function SignUpPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [errors, setErrors] = useState<Errors>({});

  const mutation = useMutation({
    mutationFn: api.signUp,
    onSuccess: async (me) => {
      qc.setQueryData(keys.me, me);
      await navigate({ to: "/onboarding/org" });
    },
    onError: (err) => {
      if (err instanceof ApiError) setErrors(err.fields);
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget));
    const parsed = SignUpInput.safeParse(data);
    if (!parsed.success) return setErrors(fieldErrors(parsed.error.issues));
    setErrors({});
    mutation.mutate(parsed.data);
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle={
        <>
          Already have one?{" "}
          <Link to="/sign-in" search={{}} className="font-medium text-accent underline-offset-4 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <FormError message={mutation.isError && !Object.keys(errors).length ? errorMessage(mutation.error) : null} />
        <Field label="Your name" error={errors.name}>
          <Input name="name" autoComplete="name" required autoFocus />
        </Field>
        <Field label="Work email" error={errors.email}>
          <Input name="email" type="email" autoComplete="email" inputMode="email" required />
        </Field>
        <Field label="Password" error={errors.password} hint="At least 10 characters.">
          <PasswordInput name="password" autoComplete="new-password" minLength={10} required />
        </Field>
        <Button type="submit" size="lg" className="mt-2 w-full" disabled={mutation.isPending}>
          {mutation.isPending && <Loader2 className="animate-spin" aria-hidden />}
          {mutation.isPending ? "Creating account…" : "Create account"}
        </Button>
        <p className="text-xs text-fg-faint">
          Seats are free on every plan. You can invite your team once your project is set up.
        </p>
      </form>
    </AuthShell>
  );
}
